// The Codex (docs/09 Phase 5): the Almanac (silhouettes until discovered), achievements, lifetime
// stats and the last 20 runs.
import { useState } from 'react';
import { ACHIEVEMENT_LIST, ALMANAC_SYMBOLS } from '../../content/achievements';
import { B_PARAMS } from '../../content/balance';
import { CHARM_LIST, COLLECTOR_LIST, RELIC_LIST } from '../../content/registry';
import { SPRITES_BY_ID } from '../../art/sprites';
import { t } from '../../i18n/t';
import { setUI, useGame } from '../../state/store';
import { Btn, Frame, Icon, KText, spriteUrl, useFmt } from '../common';
import { tip } from '../tooltip/Tooltip';

type Tab = 'almanac' | 'achievements' | 'stats';

/** An undiscovered entry: the sprite's outline in a flat palette colour. */
function Silhouette({ id }: { id: string }) {
  const def = SPRITES_BY_ID.get(id);
  if (!def) return null;
  const url = spriteUrl(id);
  return (
    <div
      className="silhouette"
      style={{ width: def.w, height: def.h, WebkitMaskImage: `url(${url})`, maskImage: `url(${url})` }}
      {...tip(<span>{t('ui.ach.secret')}</span>)}
    />
  );
}

function Entry({ sprite, known, name, desc }: { sprite: string; known: boolean; name: string; desc?: string }) {
  if (!known) return <Silhouette id={sprite} />;
  return (
    <div
      {...tip(
        <div className="col">
          <span className="h">{name}</span>
          {desc && <KText text={desc} />}
        </div>,
      )}
    >
      <Icon id={sprite} />
    </div>
  );
}

function Section({ title, found, total, children }: { title: string; found: number; total: number; children: React.ReactNode }) {
  return (
    <div className="col" style={{ gap: 3 }}>
      <div className="row">
        <span className="h">{title}</span>
        <span className="dim">
          {found}/{total}
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>{children}</div>
    </div>
  );
}

function Almanac() {
  const meta = useGame((g) => g.meta);
  const a = meta.almanac;
  const collectors = COLLECTOR_LIST.filter((c) => !c.final);
  return (
    <div className="col" style={{ gap: 8 }}>
      <Section title={t('ui.codex.symbols')} found={ALMANAC_SYMBOLS.filter((id) => a.symbols.includes(id)).length} total={ALMANAC_SYMBOLS.length}>
        {ALMANAC_SYMBOLS.map((id) => (
          <Entry key={id} sprite={`sym_${id}`} known={a.symbols.includes(id)} name={t(`sym.${id}.name`)} desc={t(`sym.${id}.desc`, B_PARAMS)} />
        ))}
      </Section>
      <Section title={t('ui.codex.charms')} found={CHARM_LIST.filter((c) => a.charms.includes(c.id)).length} total={CHARM_LIST.length}>
        {CHARM_LIST.map((c) => (
          <Entry key={c.id} sprite={`charm_${c.id}`} known={a.charms.includes(c.id)} name={t(`charm.${c.id}.name`)} desc={t(`charm.${c.id}.desc`)} />
        ))}
      </Section>
      <Section title={t('ui.codex.relics')} found={RELIC_LIST.filter((r) => meta.relics.owned.includes(r.id)).length} total={RELIC_LIST.length}>
        {RELIC_LIST.map((r) => (
          <Entry key={r.id} sprite={`relic_${r.id}`} known={meta.relics.owned.includes(r.id)} name={t(`relic.${r.id}.name`)} desc={t(`relic.${r.id}.desc`)} />
        ))}
      </Section>
      <Section title={t('ui.codex.collectors')} found={collectors.filter((c) => a.collectors.includes(c.id)).length} total={collectors.length}>
        {collectors.map((c) => (
          <Entry key={c.id} sprite={`portrait_${c.id}`} known={a.collectors.includes(c.id)} name={t(`collector.${c.id}.name`)} desc={t(`collector.${c.id}.rule`)} />
        ))}
      </Section>
    </div>
  );
}

function Achievements() {
  const earned = useGame((g) => g.meta.achievements);
  return (
    <div className="col" style={{ gap: 3 }}>
      <div className="dim">{t('ui.ach.count', { n: earned.length, total: ACHIEVEMENT_LIST.length })}</div>
      {ACHIEVEMENT_LIST.map((a) => {
        const done = earned.includes(a.id);
        const hidden = a.secret && !done;
        return (
          <div key={a.id} className={`ach ${done ? 'ach-done' : ''}`} data-testid={`ach-${a.id}`}>
            <span className="h">{hidden ? t('ui.ach.secret') : t(`ach.${a.id}.name`)}</span>
            <span className="dim">{hidden ? t('ui.ach.secretDesc') : t(`ach.${a.id}.desc`)}</span>
          </div>
        );
      })}
    </div>
  );
}

function Stats() {
  const meta = useGame((g) => g.meta);
  const fmt = useFmt();
  const l = meta.lifetime;
  const bonuses = Object.values(l.bonuses).reduce((a, b) => a + b, 0);
  const rows: [string, string | number][] = [
    ['ui.stats.consumed', l.consumed],
    ['ui.stats.maxTribute', l.maxTribute],
    ['ui.stats.souls', fmt(l.soulsEarned)],
    ['ui.stats.spins', fmt(l.spins)],
    ['ui.stats.perfect', fmt(l.perfectPulls)],
    ['ui.stats.bonuses', bonuses],
    ['ui.stats.reckonings', l.reckoningsBeaten],
    ['ui.stats.deals', `${l.dealsAccepted} / ${l.dealsRefused}`],
    ['ui.stats.molts', meta.molts],
    ['ui.stats.teeth', l.teethEarned],
    ['ui.stats.devours', `${l.devours} / ${l.miracles}`],
    ['ui.stats.endings', l.endings.length],
    ['ui.stats.endless', l.bestEndless],
  ];
  return (
    <div className="col" style={{ gap: 8 }}>
      <div className="col" style={{ gap: 1 }}>
        {rows.map(([k, v]) => (
          <div key={k} className="row">
            <span>{t(k)}</span>
            <span className="h">{v}</span>
          </div>
        ))}
      </div>
      <div className="h">{t('ui.stats.history')}</div>
      <div className="col" style={{ gap: 1 }} data-testid="history">
        {(meta.history ?? []).length === 0 && <div className="dim">{t('ui.stats.noRuns')}</div>}
        {(meta.history ?? []).map((r, i) => (
          <div key={i} className="row">
            <span>{t('ui.stats.run', { n: r.n })}</span>
            <span className="dim">{t(`ui.size.${r.size}`)}</span>
            <span className={r.reason === 'ending' ? 'kw-value' : r.reason === 'offer' ? 'dim' : 'kw-bad'}>
              {r.reason === 'ending' ? t(`ui.ending.name.${r.ending ?? 'sated'}`) : t(`ui.stats.reason.${r.reason}`)}
            </span>
            <span style={{ color: '#a884f3' }}>+{fmt(r.souls)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CodexPanel() {
  const [tab, setTab] = useState<Tab>('almanac');
  return (
    <div className="overlay interactive" onClick={() => setUI({ panel: null })}>
      <Frame title={t('ui.codex.title')} style={{ width: 460, maxHeight: 316 }}>
        <div onClick={(e) => e.stopPropagation()}>
          <div className="row" style={{ justifyContent: 'flex-start', gap: 4, marginBottom: 4 }}>
            {(['almanac', 'achievements', 'stats'] as Tab[]).map((k) => (
              <Btn key={k} kind={tab === k ? 'primary' : 'ghost'} onClick={() => setTab(k)} testId={`codex-${k}`}>
                {t(`ui.codex.${k}`)}
              </Btn>
            ))}
          </div>
          <div className="scroll" style={{ maxHeight: 256 }}>
            {tab === 'almanac' && <Almanac />}
            {tab === 'achievements' && <Achievements />}
            {tab === 'stats' && <Stats />}
          </div>
        </div>
        <div style={{ position: 'absolute', right: 4, top: 3 }}>
          <Btn kind="ghost" onClick={() => setUI({ panel: null })}>
            ✕
          </Btn>
        </div>
      </Frame>
    </div>
  );
}
